import React, { useState } from 'react';
import { Card, Button, Table, Space, Tag, Input, Modal, Form, Typography, Progress } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, CheckCircleOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCAPAs, useCloseCAPA } from '../../hooks/useQuality';
import { CAPARecord } from '../../services/quality.service';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Search } = Input;
const { TextArea } = Input;

const CAPAsPage: React.FC = () => {
  const navigate = useNavigate();
  const [isCloseModalVisible, setIsCloseModalVisible] = useState(false);
  const [selectedCAPA, setSelectedCAPA] = useState<CAPARecord | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');

  const { data: capas = [], isLoading, refetch } = useCAPAs();
  const closeMutation = useCloseCAPA();

  const handleClose = (capa: CAPARecord) => {
    setSelectedCAPA(capa);
    form.resetFields();
    setIsCloseModalVisible(true);
  };

  const handleCloseSubmit = async (values: any) => {
    try {
      await closeMutation.mutateAsync({ id: selectedCAPA!.id, notes: values.notes });
      setIsCloseModalVisible(false);
      form.resetFields();
      refetch();
    } catch (error) {
      console.error('Error closing CAPA:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Assigned: 'blue',
      'In Progress': 'processing',
      Completed: 'success',
      'Effectiveness Verification': 'purple',
      Closed: 'success',
      Cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const filteredCAPAs = capas.filter((capa) =>
    capa.capa_number.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns: ColumnsType<CAPARecord> = [
    {
      title: 'CAPA Number',
      dataIndex: 'capa_number',
      key: 'capa_number',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Progress',
      dataIndex: 'progress_percentage',
      key: 'progress_percentage',
      render: (progress: number) => <Progress percent={progress} size="small" />,
    },
    {
      title: 'Target Date',
      dataIndex: 'target_completion_date',
      key: 'target_completion_date',
      render: (date: string) => (date ? dayjs(date).format('DD/MM/YYYY') : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/qms/capas/${record.id}`)}
          >
            View
          </Button>
          {record.status !== 'Closed' && (
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              size="small"
              onClick={() => handleClose(record)}
            >
              Close
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Corrective & Preventive Actions (CAPA)</Title>

      <Card>
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
          <Search
            placeholder="Search CAPAs..."
            allowClear
            style={{ width: 300 }}
            onSearch={setSearchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/qms/capas/new')}>
            Create CAPA
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={filteredCAPAs}
          loading={isLoading}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Card>

      <Modal
        title="Close CAPA"
        open={isCloseModalVisible}
        onCancel={() => {
          setIsCloseModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleCloseSubmit}>
          <Form.Item name="notes" label="Closure Notes">
            <TextArea rows={4} placeholder="Enter closure notes" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CAPAsPage;
